import videojs from 'video.js'
import 'videojs-overlay'

const playUrl = process.env.PLAY_URL
const errorRetries = 5
let player
let tracks
let context
let errorCount = 0
let showOverlay = false

/**
 * VideoJS's XHR request intercepter. We use it to patch outgoing requests with the refreshed streaming tokens.
 * @param {*} options videojs xhr options
 * @returns patched options
 */
videojs.Vhs.xhr.beforeRequest = function (options) {
  const stream = getCurrentStream()
  const secureToken = new URL(stream.source.src).search.substr(1)
  const url = new URL(options.uri)
  showOverlay = false

  if (url.pathname.match(/\/playlist\.m3u8$/)) {
    url.search = `?${secureToken}`
    showOverlay = true
  }

  if (url.pathname.match(/^\/redirect/)) {
    url.search = `?${secureToken}`
  }

  if (url.pathname.match(/(?:_tk)[^._]+(?=\.m3u8)/)) {
    url.pathname = url.pathname.replace(
      /(?:_tk)[^._]+(?=\.m3u8)/,
      '_tk' + window.btoa(secureToken)
    )
  }
  if (url.pathname.match(/(?:_tk)[^._]+(?=_\d+.ts)/)) {
    url.pathname = url.pathname.replace(
      /(?:_tk)[^._]+(?=_\d+.ts)/,
      '_tk' + window.btoa(secureToken)
    )
  }

  options.uri = url.toString()
  return options
}

/**
 * Function to calc a randomized but good time to refresh the streaming token
 * @param {number} expires time when streaming token expires
 * @returns ms until nice time to refresh
 */
function niceRefreshTime (expires) {
  const now = Date.now()
  const halfDuration = (expires - now) * 0.5
  const half = now + halfDuration
  const last = now + (expires - now) * 0.9
  return Math.floor(halfDuration + (last - half) * Math.random())
}

/**
 * Fetches the streaming context (incl. token) and keeps it up2date.
 * @param {string} jwt initial or refresh token for play endpoint
 * @returns {void} when inital request is done
 */
async function fetchStreams (jwt) {
  const response = await window.fetch(playUrl, {
    method: 'post',
    body: JSON.stringify({ jwt })
  })
  context = await response.json()

  if (!response.ok) {
    throw context
  }

  // TODO: move this to the play cloud function
  if (player) {
    // trash call to get 403 so the other calls load correctly
    // basically it 'activates' the token by getting response 403
    window.fetch(getCurrentStream().source.src)
  }

  setTimeout(
    () => fetchStreams(context.refresh),
    niceRefreshTime(context.expires * 1000)
  )
}

/**
 * Retrieve the current stream based on the chosen audio track
 * @returns stream
 */
function getCurrentStream () {
  const track = Array.from(tracks).find(t => t.enabled)
  return context.streams.find(stream => stream.track.id === track.id)
}

/**
 * Override audio tracks explicitly and seal the AudioTrackList
 */
function setupAudioTracks () {
  Array.from(tracks).forEach(t => tracks.removeTrack(t))
  context.streams.forEach((stream, idx) => {
    tracks.addTrack(new videojs.AudioTrack(stream.track))
  })
  // seal tracklist the sneaky way: prevent videojs to change it on metadata load
  tracks.addTrack = () => {}
  tracks.removeTrack = () => {}
}

function handleError () {
  console.warn(showOverlay)
  if (showOverlay) {
    player.trigger('showOverlay')
  }

  return errorCount++ >= errorRetries
}

/**
 * Verify or fail. On success kickstart player.
 */
async function init () {
  try {
    await fetchStreams(new URL(window.location.href).searchParams.get('jwt'))
  } catch (error) {
    player = videojs('player')
    player.error(error.code || error)
    return
  }

  player = videojs('player', { controls: true })

  player.overlay({
    overlays: [
      {
        content:
          'The stream is currently not available. Please try again later.',
        align: 'bottom',
        start: 'showOverlay',
        end: 'hideOverlay'
      }
    ]
  })

  tracks = player.audioTracks()

  setupAudioTracks()
  tracks.addEventListener('change', () => {
    const time = player.currentTime()
    player.src(getCurrentStream().source)
    player.trigger('hideOverlay')
    player.play()
    // TODO: maybe don't seek in live mode?
    player.one('play', () => player.currentTime(time))
  })

  player.on('play', () => {
    player.trigger('hideOverlay')
    player.reloadSourceOnError({
      errorInterval: errorRetries,
      getSource: function (reload) {
        showOverlay = false
        if (!handleError()) {
          reload({
            src: getCurrentStream().source.src,
            type: getCurrentStream().source.sourceType
          })
        }
        showOverlay = true
      }
    })
    // reload on warning (stream not available, playlist empty)
    player.tech_.on('retryplaylist', handleError)
  })

  player.on('playing', () => {
    player.trigger('hideOverlay')
  })

  tracks.trigger('change')
  player.play()
}

init()
